// Cross-origin attribution token (TAS-371).
//
// The problem it solves: a player taps "Buy a Tasting Box" at the end of a
// game, lands on tasted.co.za — a different origin — and buys. Over 90 days 52
// players tapped that button and not one order can be traced back to the
// person who tapped it. `orders` carries `session_id` (the HOST's game) and
// nothing else, and the website's `Page View` recorded no source at all:
// breaking prod's Page View down by `src` is 100% undefined.
//
// The links already carried `src` / `session` / `player` / `mp_id` as four
// separate query params. Four params is four things to forget, four things for
// a mail client to rewrite, and no room for "which link in the email". One
// opaque `t=` carries all of it plus the campaign, and — being one value — can
// be handed to the order RPC intact and stored next to the order.
//
// Rules this file lives by:
//   - PII never enters a token. session_user_id is an opaque uuid; mp_id is a
//     Mixpanel distinct_id. No email, no name (TAS-73).
//   - Parsing is strict and total: a token that is malformed, forged, oversized
//     or simply somebody else's `t=` returns null rather than throwing. The
//     `v1.` prefix exists so /unsubscribe?t=<unsubscribe token> can never be
//     mistaken for attribution.
//   - Pure functions. No DOM, no storage, no analytics import — so the same
//     code runs in the game, the website, vitest and (conceptually) the Deno
//     edge functions.

/** Where a tokenised link was placed. Closed set — parse rejects anything else. */
export const ATTRIBUTION_SOURCES = Object.freeze([
  'game_final', // apps/game end-of-game page (ThankYouTesting)
  'game_reveal', // in-game reveal / prize surfaces
  'recap_email', // the morning recap (send-game-recap)
  'scoreboard_banner', // standings surfaces that link out
]);

/**
 * Campaigns this codebase actually mints. A closed set for the same reason
 * ATTRIBUTION_SOURCES is one: `campaign` reaches Mixpanel as an event property,
 * and the value arrives in a URL anyone can edit. `?t=` carrying
 * `{"c":"Jane Smith"}` would otherwise land a person's name in a breakdown as
 * `jane-smith` — a property we would then be storing forever, about someone who
 * never agreed to anything.
 *
 * Unknown campaigns are dropped from the ANALYTICS properties only. The raw
 * token still goes to the order, where the server re-parses it: that path is
 * not a public breakdown and the column is ours.
 */
export const ATTRIBUTION_CAMPAIGNS = Object.freeze([
  'buy_box', // "Buy a Tasting Box" — the end-of-game CTA, and the recap's
  'buy_wine', // "buy this wine" — end-of-game card, and the recap's wine cards
  'gift_box', // the recap's "send one as a gift"
  'prize_claim', // a winner's shop-with-my-discount link
  'create_account', // the recap's sign-in nudge
  'leaderboard', // standings links out
]);

/**
 * `src` values that predate the token and are still arriving from links already
 * in inboxes. Same reasoning: a legacy `?src=` is free text on the URL, so the
 * ones we minted are named and everything else is dropped from analytics.
 */
export const LEGACY_SOURCES = Object.freeze([
  'game',
  'game-prize',
  'recap',
  'recap-gift',
  'recap-prize',
  'recap-preview',
  'recap-preview-decline',
  'recap-signin',
]);

const KNOWN_SRC = new Set([...ATTRIBUTION_SOURCES, ...LEGACY_SOURCES]);
const KNOWN_CAMPAIGNS = new Set(ATTRIBUTION_CAMPAIGNS);

const TOKEN_PREFIX = 'v1.';
/** The only payload keys a v1 token may carry: source, session, user, campaign, mp_id. */
const TOKEN_KEYS = new Set(['o', 's', 'u', 'c', 'm']);
/** Generous ceiling; a real token is ~180 chars. Guards against junk `t=`. */
const MAX_TOKEN_LENGTH = 512;
const MAX_CAMPAIGN_LENGTH = 40;
const MAX_SESSION_LENGTH = 24;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Same shape adoptDistinctId() will accept on the far side; anything it would
// refuse is not worth carrying.
const MP_ID_RE = /^[A-Za-z0-9-]{8,64}$/;
const SESSION_RE = /^[A-Za-z0-9_-]{1,24}$/;

/**
 * Campaigns are free text at the call site ("buy_box", "leaderboard"), so they
 * are slugged before they can reach a URL, an event property or a database
 * column. Same conservative slug the utm_* handling in analytics-logger uses.
 */
export function slugCampaign(value) {
  if (value === null || value === undefined) return null;
  const slug = String(value)
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_CAMPAIGN_LENGTH);
  return slug || null;
}

// ── base64url, without depending on a runtime that has one ──────────────────
// Payloads are ASCII by construction (every field is validated above), so
// btoa/atob are safe; Buffer is the fallback for a bare Node runtime.

function b64urlEncode(ascii) {
  const b64 =
    typeof btoa === 'function'
      ? btoa(ascii)
      : // eslint-disable-next-line no-undef
        Buffer.from(ascii, 'binary').toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlDecode(token) {
  let b64 = token.replace(/-/g, '+').replace(/_/g, '/');
  b64 += '='.repeat((4 - (b64.length % 4)) % 4);
  if (typeof atob === 'function') return atob(b64);
  // eslint-disable-next-line no-undef
  return Buffer.from(b64, 'base64').toString('binary');
}

/**
 * Build the `t=` value for one link, or null when there is nothing worth
 * carrying. A link with no session and no player still gets a token when it has
 * a source and campaign — knowing the click came from the recap's leaderboard
 * button is worth something on its own.
 *
 * @param {object} fields
 * @param {string} [fields.sessionId]     the game code (sessions_v2.id)
 * @param {string} [fields.sessionUserId] the player (session_users_v2.id, uuid)
 * @param {string}  fields.source         one of ATTRIBUTION_SOURCES
 * @param {string} [fields.campaign]      which link, e.g. 'buy_box'
 * @param {string} [fields.mpId]          Mixpanel distinct_id to hand over
 * @returns {string|null}
 */
export function buildAttributionToken({
  sessionId,
  sessionUserId,
  source,
  campaign,
  mpId,
} = {}) {
  if (!ATTRIBUTION_SOURCES.includes(source)) return null;

  const payload = { o: source };
  const s = sessionId === null || sessionId === undefined ? '' : String(sessionId).trim();
  if (s && SESSION_RE.test(s)) payload.s = s.slice(0, MAX_SESSION_LENGTH);
  const u = sessionUserId ? String(sessionUserId).trim() : '';
  if (u && UUID_RE.test(u)) payload.u = u.toLowerCase();
  const c = slugCampaign(campaign);
  if (c) payload.c = c;
  const m = mpId ? String(mpId).trim() : '';
  if (m && MP_ID_RE.test(m)) payload.m = m;

  try {
    return TOKEN_PREFIX + b64urlEncode(JSON.stringify(payload));
  } catch {
    return null;
  }
}

/**
 * Decode a `t=` value. Returns snake_case fields — the same names the events,
 * the columns and the RPC use — or null for anything that is not one of ours.
 *
 * @returns {{session_id: string|null, session_user_id: string|null,
 *           source: string, campaign: string|null, mp_id: string|null}|null}
 */
export function parseAttributionToken(token) {
  if (typeof token !== 'string') return null;
  const raw = token.trim();
  if (!raw.startsWith(TOKEN_PREFIX)) return null;
  if (raw.length > MAX_TOKEN_LENGTH) return null;
  const body = raw.slice(TOKEN_PREFIX.length);
  if (!body || !/^[A-Za-z0-9_-]+$/.test(body)) return null;

  let payload;
  try {
    payload = JSON.parse(b64urlDecode(body));
  } catch {
    return null;
  }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;

  // Exactly the keys buildAttributionToken writes, and nothing else. An extra
  // field changes nothing we READ, but it changes the token TEXT — and a
  // forger rotating `{"n":1}`, `{"n":2}`… would otherwise mint unlimited
  // distinct, valid tokens from one identity. Kept in lockstep with
  // parse_attribution_token in migration 188; a test pins both.
  if (Object.keys(payload).some((k) => !TOKEN_KEYS.has(k))) return null;

  const source = typeof payload.o === 'string' ? payload.o : '';
  if (!ATTRIBUTION_SOURCES.includes(source)) return null;

  const sessionId = typeof payload.s === 'string' && SESSION_RE.test(payload.s) ? payload.s : null;
  const sessionUserId =
    typeof payload.u === 'string' && UUID_RE.test(payload.u) ? payload.u.toLowerCase() : null;
  const campaign = typeof payload.c === 'string' ? slugCampaign(payload.c) : null;
  const mpId = typeof payload.m === 'string' && MP_ID_RE.test(payload.m) ? payload.m : null;

  return {
    session_id: sessionId,
    session_user_id: sessionUserId,
    source,
    campaign,
    mp_id: mpId,
  };
}

/**
 * Read attribution out of a query string: the token first, the pre-TAS-371
 * `src` / `session` / `player` / `mp_id` params as the fallback.
 *
 * The legacy branch is not a courtesy — links already in inboxes and already
 * shared carry those params and will keep arriving for as long as people open
 * old recaps. A legacy `src` is free text (`game`, `recap-gift`, `game-prize`,
 * …), so it is slugged rather than checked against ATTRIBUTION_SOURCES; only
 * tokens we mint are held to the closed set.
 *
 * @param {string|URLSearchParams} search
 * @returns {{src: string|null, campaign: string|null, session_id: string|null,
 *           player_id: string|null, mp_id: string|null, token: string|null}|null}
 */
export function readAttribution(search) {
  let params;
  try {
    params = search instanceof URLSearchParams ? search : new URLSearchParams(search || '');
  } catch {
    return null;
  }

  const parsed = parseAttributionToken(params.get('t'));
  if (parsed) {
    return {
      src: parsed.source,
      campaign: parsed.campaign,
      session_id: parsed.session_id,
      player_id: parsed.session_user_id,
      mp_id: parsed.mp_id,
      token: params.get('t'),
    };
  }

  const src = slugCampaign(params.get('src'));
  if (!src) return null;
  const session = params.get('session');
  const player = params.get('player');
  const mp = params.get('mp_id');
  return {
    src,
    campaign: slugCampaign(params.get('campaign')),
    session_id: session && SESSION_RE.test(session) ? session : null,
    player_id: player && UUID_RE.test(player) ? player.toLowerCase() : null,
    mp_id: mp && MP_ID_RE.test(mp) ? mp : null,
    token: null,
  };
}

/**
 * The four properties every attributed analytics event carries. Empty object
 * when there is no attribution, so it can be spread unconditionally — a null
 * registered as a super property would haunt the breakdowns forever.
 *
 * `player_id` is session_users_v2.id: an opaque uuid, never PII (TAS-73).
 */
export function attributionEventProps(attribution) {
  if (!attribution) return {};
  const out = {};
  // src and campaign are allowlisted, not merely slugged. Both arrive in a URL
  // anyone can edit, and both become Mixpanel properties — a slug is a
  // sanitiser, not a filter, and `?src=Jane%20Smith` slugs perfectly happily
  // into a breakdown bucket named after a person.
  if (attribution.src && KNOWN_SRC.has(attribution.src)) out.src = attribution.src;
  if (attribution.campaign && KNOWN_CAMPAIGNS.has(attribution.campaign)) {
    out.campaign = attribution.campaign;
  }
  // session_id and player_id are already shape-checked on the way in (a game
  // code, a uuid) and carry no free text.
  if (attribution.session_id) out.session_id = attribution.session_id;
  if (attribution.player_id) out.player_id = attribution.player_id;
  return out;
}

/**
 * Every property name attributionEventProps can produce.
 *
 * Callers registering these as Mixpanel super properties need the full set, not
 * just the ones they have: a later visit carrying only a source must UNREGISTER
 * the previous visit's player and session rather than leave them standing. A
 * stale super property is worse than a missing one — it is a wrong answer that
 * looks like a right one.
 */
export const ATTRIBUTION_PROP_NAMES = Object.freeze([
  'src',
  'campaign',
  'session_id',
  'player_id',
]);

/**
 * The same token with the Mixpanel identity removed, for a visitor who declined
 * tracking (TAS-252).
 *
 * `m` is a Mixpanel distinct_id. Keeping one in a 30-day cookie for somebody who
 * asked not to be tracked is the thing they asked us not to do — and the server
 * never reads it: parse_attribution_token (migration 188) returns four fields
 * and `m` is not among them. So it costs nothing to drop.
 *
 * Returns the re-minted token, or null if the input was not one of ours.
 */
export function stripIdentityFromToken(token) {
  const parsed = parseAttributionToken(token);
  if (!parsed) return null;
  return buildAttributionToken({
    sessionId: parsed.session_id,
    sessionUserId: parsed.session_user_id,
    source: parsed.source,
    campaign: parsed.campaign,
  });
}

/**
 * Append `t=` (and the legacy params, for the transition) to a URL.
 *
 * Anchors are handled explicitly: the in-game buy button deep-links to
 * `#boxes`, and a query string appended AFTER a fragment is read by the browser
 * as part of the fragment — the params then never arrive at all.
 */
export function withAttribution(url, fields = {}) {
  const token = buildAttributionToken(fields);
  const legacy = fields.legacySrc ?? null;
  if (!token && !legacy) return url;

  const str = String(url);
  const hashAt = str.indexOf('#');
  const hash = hashAt === -1 ? '' : str.slice(hashAt);
  const base = hashAt === -1 ? str : str.slice(0, hashAt);
  const [path, existing = ''] = base.split('?');

  const params = new URLSearchParams(existing);
  if (token) params.set('t', token);
  if (legacy) {
    if (!params.has('src')) params.set('src', legacy);
    if (fields.sessionId && !params.has('session')) params.set('session', String(fields.sessionId));
    if (fields.sessionUserId && !params.has('player')) params.set('player', String(fields.sessionUserId));
    if (fields.mpId && !params.has('mp_id')) params.set('mp_id', String(fields.mpId));
  }
  const qs = params.toString();
  return `${path}${qs ? `?${qs}` : ''}${hash}`;
}

// ── Taking the token off the URL ─────────────────────────────────────────────
//
// Mixpanel stamps `$current_url` onto every event automatically, and
// analytics-hooks logs `location.search` in dev. Left in the address bar, `t=`
// would ride into analytics on every event of the visit and into any
// screenshot, support paste or shared link — carrying the player's uuid with
// it. The codebase already does exactly this for `email` and `mp_id`
// (metaPixel.scrubSensitiveParamsFromUrl); this is the same move for a
// parameter whose name we do not own exclusively.
//
// Only a token that PARSES is taken. /unsubscribe reads a `t=` of its own, and
// removing that would break the one-click unsubscribe RFC 8058 requires.

let _stashedToken = null;
let _captured = false;

/**
 * Capture `t=` and remove it from the address bar. Idempotent, safe before
 * React renders, and a no-op anywhere without a DOM.
 *
 * Called by analytics-logger at module load — i.e. before the first event —
 * and again by each app's own reader, which is why it has to be idempotent.
 *
 * @returns {string|null} the captured token
 */
export function captureAndStripToken() {
  if (_captured) return _stashedToken;
  _captured = true;
  if (typeof window === 'undefined' || !window.history?.replaceState) return null;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get('t');
    // Not ours — the unsubscribe page's token, or junk. Leave it alone.
    if (!raw || !parseAttributionToken(raw)) return null;
    _stashedToken = raw;
    url.searchParams.delete('t');
    window.history.replaceState(window.history.state, '', url.toString());
    return _stashedToken;
  } catch {
    // A URL we cannot parse is a URL we cannot strip; the token simply stays,
    // which is the pre-TAS-371 behaviour and not worth throwing over.
    return null;
  }
}

/** The token taken off the URL this load, or null. */
export function getStashedToken() {
  return _stashedToken;
}

/**
 * This visit's attribution: the stashed token first (it is no longer on the URL
 * to be re-read), then whatever the query string still says.
 */
export function readVisitAttribution() {
  if (typeof window === 'undefined') return null;
  const token = captureAndStripToken();
  try {
    const params = new URLSearchParams(window.location.search);
    if (token) params.set('t', token);
    return readAttribution(params);
  } catch {
    return null;
  }
}

/** Test seam: forget the stash so the next call re-reads the URL. */
export function __resetTokenCapture() {
  _captured = false;
  _stashedToken = null;
}
